import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import './App.css';
import 'antd/dist/reset.css';

import AppRoutes from './components/Routes/AppRoutes';
import CookieConsent from './components/CookieConsent/CookieConsent';
import LogIn from './pages/Auth/login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Invitation from './pages/Invitation/Invitation';
import { clearAuthSession, getJwtToken, getStoredUser, isAuthenticated } from './utilities/authentication';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { loadLanguage } from './store/Language/LanguageSlice';
import { getThemeMode } from './store/Theme/ThemeSlice';
import config from './config';

const getRequestedLogin = (searchParams) =>
  String(searchParams.get('username') || searchParams.get('login') || searchParams.get('email') || '')
    .trim()
    .toLowerCase();

const isSignedInAs = (identifier) => {
  const user = getStoredUser();
  if (!user || typeof user !== 'object') {
    return false;
  }
  return [user.username, user.email]
    .filter(Boolean)
    .some((value) => String(value).trim().toLowerCase() === identifier);
};

/**
 * Signed-in users hitting /login go to the dashboard, unless the link asks for a
 * different account (e.g. switching between the lab and clinic demos on the
 * marketing site) — then the current session is dropped and the login form shown.
 */
const AuthenticatedLoginRoute = () => {
  const [searchParams] = useSearchParams();
  const requested = getRequestedLogin(searchParams);
  const mustSwitch = Boolean(requested) && !isSignedInAs(requested);

  useEffect(() => {
    if (!mustSwitch) {
      return;
    }
    const token = getJwtToken();
    if (token) {
      fetch(`${config.apiUrl}/logout`, {
        method: 'POST',
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {});
    }
    clearAuthSession();
    window.location.replace(`/login?${searchParams.toString()}`);
  }, [mustSwitch, searchParams]);

  return mustSwitch ? null : <Navigate to="/dashboard" replace />;
};


function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadLanguage());
    dispatch(getThemeMode());
  }, [dispatch]);

  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public invitation route (accessible without auth) */}
          <Route path='/invite/:token' element={<Invitation />} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          
          {isAuthenticated() ? (
            <>
              {/* Demo / login links from the marketing site land here when already signed in */}
              <Route path='/login' element={<AuthenticatedLoginRoute />} />
              <Route path='/*' element={<AppRoutes />} />
            </>
          )
            :
            <>
              <Route path='/' element={<LogIn />} />
              <Route path='/login' element={<LogIn />} />
              <Route path='*' element={<LogIn />} />
            </>
          }
        </Routes>
      </BrowserRouter>
      <CookieConsent />
    </>
  );
}

export default App;
